import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FileText } from "lucide-react";

import { type SelfAssessmentItem } from "./selfAssessmentPublishHelpers";
import DropdownSwitcher from "../../Components/DropdownSwitcher";
import SearchInput from "../../Components/Search/SearchInput";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchVendorSelfAssessments } from "../../Store/Reducers/AssessmentSlice";

/** GET /assessments/self-assessments: open (PRE_ACTIVE; PRE_PUBLISH leftovers for back-compat). */
const OPEN_HUB_STATUS = "open";

const TYPE_FILTER_OPTIONS = [
  { label: "All Types", value: "all" },
  { label: "Short Assessment", value: "short" },
  { label: "Long Assessment", value: "long" },
];

export type TeamAssessmentHubItem = SelfAssessmentItem & {
  category?: string | null;
};

interface SelfAssessmentsHubTabProps {
  renderCard: (
    item: TeamAssessmentHubItem,
    isExpanded: boolean,
    onToggleExpand: () => void
  ) => React.ReactNode;
}

const SelfAssessmentsHubTab: React.FC<SelfAssessmentsHubTabProps> = ({
  renderCard,
}) => {
  const dispatch = useAppDispatch();
  const {
    vendorSelfAssessments,
    isLoadingVendorSelfAssessments,
    vendorSelfAssessmentsError,
  } = useAppSelector(s => s.assessment);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    dispatch(
      fetchVendorSelfAssessments({
        status: OPEN_HUB_STATUS,
        sort_by: "created_at",
        sort_order: "desc",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const list = useMemo(
    () => (vendorSelfAssessments || []) as TeamAssessmentHubItem[],
    [vendorSelfAssessments]
  );

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return list.filter(a => {
      const category = (a.category || "short").toLowerCase();
      const matchesType =
        selectedTypeFilter === "all" ||
        (selectedTypeFilter === "short"
          ? category === "short" || category === "small"
          : category === "long" || category === "large");
      if (!matchesType) return false;
      if (!q) return true;
      if (
        a.name?.toLowerCase().includes(q) ||
        a.title?.toLowerCase().includes(q)
      ) {
        return true;
      }
      return a.target_accounts?.some(
        acc =>
          acc.account_name?.toLowerCase().includes(q) ||
          acc.users?.some(
            u =>
              u.name.toLowerCase().includes(q) ||
              u.email.toLowerCase().includes(q)
          )
      );
    });
  }, [list, searchQuery, selectedTypeFilter]);

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-end gap-3 flex-wrap shrink-0 mb-5">
        <div className="min-w-[10rem] w-52 sm:w-72">
          <SearchInput
            placeholder="Search assessments..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <DropdownSwitcher
          className="shrink-0"
          value={selectedTypeFilter}
          prefix="Type: "
          options={TYPE_FILTER_OPTIONS}
          onChange={setSelectedTypeFilter}
          listboxLabel="Type"
        />
      </div>

      {vendorSelfAssessmentsError ? (
        <p className="mb-4 text-sm text-red-600">
          {vendorSelfAssessmentsError}
        </p>
      ) : null}

      {isLoadingVendorSelfAssessments ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
          <p className="text-gray-600 mt-2">Loading assessments...</p>
        </div>
      ) : !filtered.length ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] py-12">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-base font-medium text-gray-600">
            {searchQuery.trim() || selectedTypeFilter !== "all"
              ? "No team assessments match your search or filter criteria."
              : "No team assessments found."}
          </p>
          {searchQuery.trim() || selectedTypeFilter !== "all" ? (
            <p className="text-sm text-gray-500 mt-2">
              Try adjusting your search or filter to see more results.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {filtered.map(item => (
            <React.Fragment key={item.assessment_id}>
              {renderCard(item, expandedIds.has(item.assessment_id), () =>
                toggleExpanded(item.assessment_id)
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelfAssessmentsHubTab;
